/**
 * Goal Score PR-7b — Studio authoring API client.
 *
 * A NEW module, deliberately separate from `templateApi.ts` (parallel-build
 * discipline: the sibling PR-7a agent is editing results/leaderboard/
 * dashboard surfaces in parallel; keeping every new Goal-authoring call in
 * its own file means zero merge-conflict surface with that work and with
 * `templateApi.ts`'s own existing travel-preview call, which this file
 * reuses unchanged rather than duplicating).
 *
 * Every function here calls a route that already exists in the deployed
 * backend (PR-2 through PR-6 all shipped dark/additive) — this PR wires the
 * Studio UI to them, it does not add new backend surface EXCEPT the
 * per-calibration-take measurement endpoint (`measureCalibrationTakeGoals`
 * below), which this PR's own backend half adds.
 */

import { apiClient } from './api';
import type {
  GoalsPreviewRequest,
  DistanceGoalPreviewResponse,
  SpeedGoalPreviewResponse,
  AngleGoalPreviewResponse,
  FormEffectCurvePreviewRequest,
  FormEffectCurvePreviewResponse,
  TakeMeasureGoalsResponse,
} from '@/types/goalAuthoring';

/**
 * Generalized Goal preview (`POST /templates/{id}/goals/preview`,
 * cache-only — never triggers a pose extraction). `kind` discriminates the
 * response shape; the backend declares no shared `response_model` for the
 * same reason this function's return type is a union rather than one
 * flattened interface — call-site code must narrow on `status`/the fields
 * it expects for the `kind` it asked for, not assume every field exists.
 *
 * Call on an explicit "measure" action and whenever the landmark/joint/
 * phase selection changes; never on every keystroke (same rule
 * `previewTravelTarget` already documents).
 */
export async function previewGoal(
  templateId: string,
  request: GoalsPreviewRequest & { kind: 'distance' },
): Promise<DistanceGoalPreviewResponse>;
export async function previewGoal(
  templateId: string,
  request: GoalsPreviewRequest & { kind: 'speed' },
): Promise<SpeedGoalPreviewResponse>;
export async function previewGoal(
  templateId: string,
  request: GoalsPreviewRequest & { kind: 'angle' },
): Promise<AngleGoalPreviewResponse>;
export async function previewGoal(
  templateId: string,
  request: GoalsPreviewRequest,
): Promise<DistanceGoalPreviewResponse | SpeedGoalPreviewResponse | AngleGoalPreviewResponse> {
  const response = await apiClient.post<
    DistanceGoalPreviewResponse | SpeedGoalPreviewResponse | AngleGoalPreviewResponse
  >(`/templates/${templateId}/goals/preview`, request);
  return response.data;
}

/**
 * Sample a coach-authored (possibly in-progress, unsaved) Form-effect curve
 * at the given Form values (`POST /templates/{id}/form-effect-curve/
 * preview`). Recompute-fresh, never cached — the chart and the
 * worked-preview sentence must never drift from what scoring will actually
 * compute, so this is the ONLY place the Studio ever gets a multiplier
 * number from; it must never recompute PCHIP itself client-side.
 *
 * Works regardless of whether `SPORTFORM_GOAL_SCORE` is armed — previewing
 * a curve under authoring is harmless either way.
 */
export async function previewFormEffectCurve(
  templateId: string,
  request: FormEffectCurvePreviewRequest,
): Promise<FormEffectCurvePreviewResponse> {
  const response = await apiClient.post<FormEffectCurvePreviewResponse>(
    `/templates/${templateId}/form-effect-curve/preview`,
    request,
  );
  return response.data;
}

/**
 * Measure every configured Goal on this template against one calibration
 * take's own pose cache (`POST /templates/{id}/calibration-takes/{take_id}
 * /measure-goals`, NEW this PR). Recompute-fresh, never cached, never
 * persisted — the travel-preview endpoint's own rule, doubly justified
 * here: a Goal's target can change after a template edit, and the 2026-09
 * calibration-worker rule-channel audit (LFT-102/103/122) already showed
 * the cost of a stale confidence/declaration value is high.
 *
 * Coach/admin-only (`can_write_template`) — 403 for a coach who does not
 * own this template or share its channel.
 */
export async function measureCalibrationTakeGoals(
  templateId: string,
  takeId: string,
): Promise<TakeMeasureGoalsResponse> {
  const response = await apiClient.post<TakeMeasureGoalsResponse>(
    `/templates/${templateId}/calibration-takes/${takeId}/measure-goals`,
  );
  return response.data;
}
